// Updated: accept .webp/.png/.jpg uploads up to 20MB, auto-convert to WebP at 80% quality; active upload feedback via spinner label and optimistic preview tile
import React, { useEffect, useState } from 'react';
import { supabase } from '../../../lib/supabase';
import { Image as ImageIcon, Plus, Trash2, Upload, Loader2 } from 'lucide-react';
import Collapsible from '../components/Collapsible';
import RichTextEditor from '../../RichTextEditor';
import { useToastContext } from '../../../contexts/ToastContext';
import { validateImageFile, convertToWebp } from '../../../utils/imageUpload';

interface GalleryImage {
  id: string;
  url: string;
  display_order: number;
}

interface GalleryText {
  title: string;
  left_text: string;
  right_text: string;
}

const GallerySection = () => {
  const [images, setImages] = useState<GalleryImage[]>([]);
  const [texts, setTexts] = useState<GalleryText>({
    title: '',
    left_text: '',
    right_text: ''
  });
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [previewUrl, setPreviewUrl] = useState('');
  const [editorKeys, setEditorKeys] = useState({ left: 0, right: 0 });
  const { addToast } = useToastContext();

  useEffect(() => {
    fetchImages();
    fetchTexts();
  }, []);

  const fetchTexts = async () => {
    try {
      const { data: titleData } = await supabase
        .from('settings')
        .select('value')
        .eq('key', 'gallery_title')
        .single();

      const { data: leftTextData } = await supabase
        .from('settings')
        .select('value')
        .eq('key', 'gallery_left_text')
        .single();

      const { data: rightTextData } = await supabase
        .from('settings')
        .select('value')
        .eq('key', 'gallery_right_text')
        .single();

      const newLeftText = leftTextData?.value || '';
      const newRightText = rightTextData?.value || '';

      setTexts({
        title: titleData?.value || '',
        left_text: newLeftText,
        right_text: newRightText
      });
      setEditorKeys(prev => ({
        left: prev.left + 1,
        right: prev.right + 1
      }));
    } catch (error) {
      console.error('Error fetching gallery texts:', error);
    }
  };

  const updateText = async (key: 'gallery_title' | 'gallery_left_text' | 'gallery_right_text', value: string) => {
    try {
      const { error } = await supabase
        .from('settings')
        .upsert({ 
          key,
          value
        }, {
          onConflict: 'key'
        });

      if (error) throw error;
      addToast('Tekst succesvol bijgewerkt', 'success');
    } catch (error) {
      console.error('Error updating text:', error);
      addToast('Er is een fout opgetreden bij het bijwerken van de tekst', 'error');
    }
  };

  const fetchImages = async () => {
    try {
      const { data, error } = await supabase
        .from('gallery')
        .select('*')
        .order('display_order', { ascending: true });

      if (error) throw error;
      setImages(data || []);
    } catch (error) {
      console.error('Error fetching images:', error);
    } finally {
      setLoading(false);
    }
  };

  const uploadImage = async (event: React.ChangeEvent<HTMLInputElement>) => {
    if (!event.target.files || event.target.files.length === 0) {
      return;
    }

    const rawFile = event.target.files[0];
    const validation = validateImageFile(rawFile);
    if (!validation.ok) {
      addToast(validation.message, 'error');
      event.target.value = '';
      return;
    }

    const objectUrl = URL.createObjectURL(rawFile);
    setPreviewUrl(objectUrl);
    setUploading(true);

    try {
      const file = await convertToWebp(rawFile);

      const fileName = `gallery-${Date.now()}.webp`;
      const filePath = `gallery/${fileName}`;

      // Upload image to Supabase Storage
      const { error: uploadError, data } = await supabase.storage
        .from('images')
        .upload(filePath, file);

      if (uploadError) throw uploadError;

      // Get public URL
      const { data: { publicUrl } } = supabase.storage
        .from('images')
        .getPublicUrl(filePath);

      const newOrder = images.length > 0 
        ? Math.max(...images.map(img => img.display_order)) + 1 
        : 0;

      // Save to gallery table
      const { error: dbError } = await supabase
        .from('gallery')
        .insert([{ 
          url: publicUrl,
          display_order: newOrder
        }]);

      if (dbError) throw dbError;
      
      fetchImages();
      addToast('Afbeelding succesvol geüpload', 'success');
    } catch (error) {
      console.error('Error adding image:', error);
      addToast('Er is een fout opgetreden bij het uploaden van de afbeelding', 'error');
    } finally {
      URL.revokeObjectURL(objectUrl);
      setPreviewUrl('');
      setUploading(false);
      event.target.value = '';
    }
  };

  const deleteImage = async (id: string) => {
    try {
      // Get the image URL first
      const { data: imageData } = await supabase
        .from('gallery')
        .select('url')
        .eq('id', id)
        .single();

      if (imageData) {
        // Extract file path from URL
        const filePath = imageData.url.split('/').pop();
        if (filePath) {
          // Delete from storage
          await supabase.storage
            .from('images')
            .remove([`gallery/${filePath}`]);
        }
      }

      // Delete from database
      const { error } = await supabase
        .from('gallery')
        .delete()
        .eq('id', id);

      if (error) throw error;
      fetchImages();
      addToast('Afbeelding succesvol verwijderd', 'success');
    } catch (error) {
      console.error('Error deleting image:', error);
      addToast('Er is een fout opgetreden bij het verwijderen van de afbeelding', 'error');
    }
  };

  if (loading) {
    return <div>Loading...</div>;
  }

  return (
    <div className="bg-white shadow rounded-lg p-6">
      <div className="mb-8 space-y-6">
        <div>
          <Collapsible title="Titel aanpassen">
            <div className="flex gap-4 items-end">
              <div className="flex-1">
                <input
                  type="text"
                  value={texts.title}
                  onChange={(e) => setTexts(prev => ({ ...prev, title: e.target.value }))}
                  className="w-full rounded-md border-gray-300 shadow-sm focus:border-[#E37C64] focus:ring-[#E37C64]"
                  placeholder="Voer de titel in"
                />
              </div>
              <button
                onClick={() => updateText('gallery_title', texts.title)}
                className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-[#E37C64] hover:bg-[#5A130E]"
              >
                Tekst bijwerken
              </button>
            </div>
          </Collapsible>
        </div>

        <div>
          <Collapsible title="Linker tekst aanpassen">
            <div className="flex gap-4 items-end">
              <div className="flex-1">
                <div className="space-y-4">
                  <RichTextEditor
                    key={editorKeys.left}
                    content={texts.left_text}
                    onChange={(value) => setTexts(prev => ({ ...prev, left_text: value }))}
                  />
                  <button
                    onClick={() => updateText('gallery_left_text', texts.left_text)}
                    className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-[#E37C64] hover:bg-[#5A130E]"
                  >
                    Tekst aanpassen
                  </button>
                </div>
              </div>
            </div>
          </Collapsible>
        </div>

        <div>
          <Collapsible title="Rechter tekst aanpassen">
            <div className="flex gap-4 items-end">
              <div className="flex-1">
                <div className="space-y-4">
                  <RichTextEditor
                    key={editorKeys.right}
                    content={texts.right_text}
                    onChange={(value) => setTexts(prev => ({ ...prev, right_text: value }))}
                  />
                  <button
                    onClick={() => updateText('gallery_right_text', texts.right_text)}
                    className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-[#E37C64] hover:bg-[#5A130E]"
                  >
                    Tekst aanpassen
                  </button>
                </div>
              </div>
            </div>
          </Collapsible>
          <Collapsible title="Galerij afbeeldingen aanpassen">
            <div className="flex gap-4 items-end mb-6">
              <label className={`flex-1 inline-flex items-center justify-center px-4 py-2 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white ${uploading ? 'opacity-60 cursor-not-allowed' : 'cursor-pointer hover:bg-gray-50'}`}>
                <input
                  type="file"
                  accept="image/webp,image/png,image/jpeg"
                  onChange={uploadImage}
                  disabled={uploading}
                  className="hidden"
                />
                {uploading ? (
                  <><Loader2 className="w-4 h-4 mr-2 animate-spin" />Bezig met uploaden...</>
                ) : (
                  'Kies bestand'
                )}
              </label>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-4">
              {uploading && previewUrl && (
                <div className="relative">
                  <img
                    src={previewUrl}
                    alt="Nieuwe afbeelding"
                    className="w-full h-48 object-cover rounded-lg opacity-70"
                  />
                  <div className="absolute inset-0 flex items-center justify-center bg-black/30 rounded-lg">
                    <Loader2 className="w-8 h-8 text-white animate-spin" />
                  </div>
                </div>
              )}
              {images.map((image) => (
                <div key={image.id} className="relative group">
                  <img
                    src={image.url}
                    alt="Gallery image"
                    className="w-full h-48 object-cover rounded-lg"
                  />
                  <button
                    onClick={() => deleteImage(image.id)}
                    className="absolute top-2 right-2 p-2 bg-red-600 text-white rounded-full opacity-0 group-hover:opacity-100 transition-opacity"
                  >
                    <Trash2 className="w-4 h-4" />
                  </button>
                </div>
              ))}
            </div>
          </Collapsible>
        </div>
      </div>
    </div>
  );
};

export default GallerySection;